import { Link } from 'react-router-dom';
import { ROUTES } from '@/lib/constants/routes';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Tag } from '@/components/ui/Tag';
import { DetailRow } from '@/components/common/DetailRow';
import { safeWebHref } from '@/lib/utils/claim.utils';
import { initialsFromName } from '@/lib/utils/user.utils';
import type { DirectoryPartnerDetail } from '../api/trusted-partners.api';
import { useContactPerk, useRedeemPerk } from '../hooks/useTrustedPartners';

import { PARTNER_COVER_BAND_ASPECT_CLASS } from '@/lib/constants/image-crop';
import { TRUSTED_PARTNERS_STRINGS as S } from '../trusted-partners.constants';
import { ContactAction } from './ContactAction';
import { PartnerCover } from './PartnerCover';
import { PerkCard } from './PerkCard';

/** A partner-supplied link row; degrades to a dash instead of rendering an unsafe or empty anchor. */
function LinkRow({ label, url }: { label: string; url: string | null }) {
  const href = safeWebHref(url);
  return (
    <DetailRow label={label}>
      {href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="break-all text-dkgreen underline underline-offset-2">
          {href}
        </a>
      ) : (
        '-'
      )}
    </DetailRow>
  );
}

/** A partner's profile page body (FR-111): identity, about, services, links, current offers, and the one contact action (FR-109). */
export function PartnerProfileView({ partner }: { partner: DirectoryPartnerDetail }) {
  const redeem = useRedeemPerk();
  const contact = useContactPerk();

  return (
    <div className="flex flex-col gap-6">
      <section className="overflow-hidden rounded-card bg-white shadow-card">
        <PartnerCover src={partner.coverPhotoUrl} className={PARTNER_COVER_BAND_ASPECT_CLASS} />
        <div className="flex flex-wrap items-start justify-between gap-4 p-6">
          <div className="flex items-start gap-4">
            <Avatar photoUrl={partner.logoUrl ?? undefined} initials={initialsFromName(partner.businessName)} size={64} />
            <div className="flex flex-col gap-1">
              <h2 className="text-xl font-semibold text-navy">{partner.businessName}</h2>
              {partner.tagline && <p className="text-p6 text-slate">{partner.tagline}</p>}
              <div className="mt-1 flex flex-wrap gap-1.5">
                {partner.categories.map((name) => (
                  <Badge key={name} variant="neutral" size="sm">
                    {name}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
          <div aria-label={S.CONTACT_HEADING}>
            <ContactAction entityId={partner.entityId} contact={partner.contact} />
          </div>
        </div>
      </section>

      <section className="rounded-card bg-white p-6 shadow-card">
        <h2 className="mb-2 text-p5 font-semibold text-navy">{S.PROFILE_ABOUT}</h2>
        <p className="whitespace-pre-line text-p6 text-navy">{partner.description}</p>
        {partner.services.length > 0 && (
          <div className="mt-4">
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate">{S.PROFILE_SERVICES}</h3>
            <div className="flex flex-wrap gap-2">
              {partner.services.map((service) => (
                <Tag key={service}>{service}</Tag>
              ))}
            </div>
          </div>
        )}
        <dl className="mt-4 flex flex-col gap-2">
          <LinkRow label={S.PROFILE_WEBSITE} url={partner.websiteUrl} />
          <LinkRow label={S.PROFILE_LINKEDIN} url={partner.linkedinUrl} />
          <LinkRow label={S.PROFILE_VIDEO} url={partner.promoVideoUrl} />
        </dl>
      </section>

      {/* Resources this partner provided (FR-294). Rendered only when there
          are any - an empty section would read as a partner with nothing to
          offer rather than one the library simply has no resources from. The
          backend caps the list and filters out hidden and deleted resources,
          so this never surfaces something the library itself withholds. */}
      {partner.providedResources.length > 0 && (
        <section aria-labelledby="partner-resources-heading">
          <h2 id="partner-resources-heading" className="mb-3 text-p5 font-semibold text-navy">
            {S.PROFILE_RESOURCES}
          </h2>
          <ul className="flex flex-col gap-2">
            {partner.providedResources.map((resource) => (
              <li key={resource.id} className="flex items-baseline justify-between gap-4">
                <span className="text-p6 text-navy">{resource.title}</span>
                {resource.folderName && (
                  <span className="text-p6 text-subtext">{resource.folderName}</span>
                )}
              </li>
            ))}
          </ul>
          <Link to={ROUTES.RESOURCES} className="text-p6 mt-3 inline-block cursor-pointer font-semibold text-primary hover:underline">
            {S.PROFILE_RESOURCES_ALL}
          </Link>
        </section>
      )}

      {partner.offers.length > 0 && (
        <section aria-labelledby="partner-offers-heading">
          <h2 id="partner-offers-heading" className="mb-3 text-p5 font-semibold text-navy">
            {S.PROFILE_OFFERS}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {partner.offers.map((perk) => (
              <PerkCard
                key={perk.id}
                perk={perk}
                onClaim={(offerId) => redeem.mutate(offerId)}
                onContact={(offerId) => contact.mutate(offerId)}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
